import React from 'react';
import { SENSOR_FUSION_DATA_LAYERS } from '@/types/shared/navigation-tabs.enums';
import { ManagedGraphsSection } from '@/modules/shared/DataLayers/components/ManagedGraphsSection';

export interface DataLayerConfig {
  id: string;
  condition?: () => boolean;
  component: () => React.ReactNode;
}

/**
 * Display Mode Configuration - defines which layers, graphs, and UI elements are visible in each mode
 *
 * DESIGN PHILOSOPHY: Support both whitelist (visible) and blacklist (hidden) approaches
 * - Use visibleLayers/visibleGraphsInLayers when you want to explicitly list what TO show
 * - Use hiddenLayers/hiddenGraphsInLayers when you want to explicitly list what NOT to show
 * - Visible takes precedence over hidden if both are specified
 *
 * @property visibleLayers - Array of layer IDs to show (empty = use activeDataLayers from metadata)
 * @property hiddenLayers - Array of layer IDs to hide but keep mounted
 * @property visibleGraphsInLayers - Per-layer filter functions that return true to SHOW a graph (whitelist)
 * @property hiddenGraphsInLayers - Per-layer filter functions that return true to HIDE a graph (blacklist)
 * @property visibleUIElements - Per-layer array of UI element IDs to show (whitelist)
 * @property hiddenUIElements - Per-layer array of UI element IDs to hide (blacklist)
 * @property autoSelectFilteredGraphs - Per-layer boolean to auto-select all filtered graphs in Manage Graphs
 */
export interface DisplayModeConfig {
  visibleLayers: string[];
  hiddenLayers?: string[];
  visibleGraphsInLayers?: Record<string, (graph: any) => boolean>;
  hiddenGraphsInLayers?: Record<string, (graph: any) => boolean>;
  visibleUIElements?: Record<string, string[]>;
  hiddenUIElements?: Record<string, string[]>;
  autoSelectFilteredGraphs?: Record<string, boolean>;
}

/**
 * Declarative configuration for all data layers
 */
export const createDataLayersConfig = (props: {
  activeDataLayers: string[];
  isMobileAsset: boolean;
  assetName?: string;
  diagnostics: any[];
  baseTitle?: string;
  from: string;
  to: string;
  enhancedQueryParams: any;
  forceDataRefresh: number;
  handleVisualAILoadingStateChange: (isLoading: boolean) => void;
  loadingStates: any;
  selectedComponentId?: string | null;
  selectedComponentObj?: any;
  setTriggerVisualAIReset: (value: boolean) => void;
  verticalLinesState?: any; // Highlighted vertical lines for SensorFusion charts
  rawVerticalLinesState?: any; // Raw vertical lines for NewSpectrum chips (SensorFusion's state)
  spectrumVerticalLinesState?: any; // Spectrum's own vertical lines state
  benchmarkLineBySection?: Record<string, string | null>; // Shared benchmark state from parent
  setBenchmarkLineBySection?: React.Dispatch<React.SetStateAction<Record<string, string | null>>>; // Setter for benchmark state
  config?: any; // Add config to get filters from backend
  legendItemStates?: Record<string, boolean>;
  toggleLegendItem?: (itemId: string, enabled: boolean) => void;
  onToggleSensor?: (sectionId: string, sensorName: string) => void;
  onAddSensor?: (sectionId: string, sensorName: string) => void;
  registerSensorToggleHandler?: (dataLayer: string, handler: (sectionId: string, sensorName: string) => void) => void;
  registerSensorData?: (
    dataLayer: string,
    enabledSensors: Record<string, boolean>,
    sensorColors: Record<string, string>
  ) => void;
  allEnabledSensors?: Record<string, Record<string, boolean>>;
  allSensorColors?: Record<string, Record<string, string>>;
  isSensorLoading?: (dataLayer: string, sectionId: string, sensorName: string) => boolean;
  // Interval lines props
  intervalLines?: any[];
  onOpenIntervalDialog?: (key: string) => void;
  onClearIntervalLines?: () => void;
  removeIntervalLine?: (id: string) => void;
  // Props for NewSpectrum
  asset?: any;
  components?: any[];
  spectrumConfig?: any;
  activeOperationalModes?: string[];
  // Refs for data layer configuration (React Rules of Hooks compliance)
  groupFilterResetRef?: React.MutableRefObject<(() => void) | null>;
  initialAlarmLegendStatesRef?: React.MutableRefObject<Record<string, boolean> | null>;
  // Global loading sensors state - synchronized across all sections
  loadingSensors?: Set<string>;
  setLoadingSensor?: (sensorName: string) => void;
  clearLoadingSensor?: (sensorName: string) => void;
  // Synced tool selection states (for syncTools feature)
  markerSelectionModeByChart?: Record<string, boolean>;
  setMarkerSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  harmonicSelectionModeByChart?: Record<string, boolean>;
  setHarmonicSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  sideBandsSelectionModeByChart?: Record<string, boolean>;
  setSideBandsSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  // Focus mode props
  legendSync?: {
    mode: 'multi' | 'focus';
    toggleMode: () => void;
    getSavedState?: () => Record<string, Record<string, boolean>> | null;
    clearSavedState?: () => void;
  };
  focusedSensors?: Record<string, string>; // Map of sectionId -> focused sensor name
  onFocus?: (sensorName: string, sectionId?: string) => void;
  // Selected marker timestamp from Spectrum for highlighting in SensorFusion
  selectedMarkerTimestamp?: string | null;
  setSelectedMarkerTimestamp?: (timestamp: string | null) => void;
  // Selected timestamps set for focus mode multi-select
  selectedTimestampsSet?: Set<string>;
  setSelectedTimestampsSet?: (timestamps: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  // Expandable legend series for sensors (vibrations)
  sensorsExpandableLegendSeries?: any[];
  onToggleExpandableSeries?: (seriesId: string) => void;
  onToggleExpandableSeriesFocus?: (seriesId: string) => void;
  onToggleExpandableTimestamp?: (seriesId: string, timestampId: string) => void;
  visualAIBeltGraphs: any[];
  visualAIRockGraphs: any[];
  visualAIBeltManager: any;
  visualAIRockManager: any;
  isTabView?: boolean;
  sectionPrimaryDataLayerId?: SENSOR_FUSION_DATA_LAYERS.rockSize | SENSOR_FUSION_DATA_LAYERS.beltMisalignment;
}): DataLayerConfig[] => {
  const {
    activeDataLayers,
    enhancedQueryParams,
    forceDataRefresh,
    handleVisualAILoadingStateChange,
    legendSync,
    focusedSensors,
    onFocus,
    visualAIBeltGraphs,
    visualAIRockGraphs,
    visualAIBeltManager,
    visualAIRockManager,
    setTriggerVisualAIReset,
    isTabView,
    config,
    sectionPrimaryDataLayerId,
  } = props;

  return [
    {
      id: SENSOR_FUSION_DATA_LAYERS.beltMisalignment,
      condition: () =>
        sectionPrimaryDataLayerId === SENSOR_FUSION_DATA_LAYERS.beltMisalignment &&
        activeDataLayers.includes(SENSOR_FUSION_DATA_LAYERS.beltMisalignment),
      component: () => {
        if (!visualAIBeltGraphs || !visualAIBeltManager) return null;
        return (
          <ManagedGraphsSection
            hideManagedGraphsDropdown={!isTabView}
            title={config.title}
            dataLayer={SENSOR_FUSION_DATA_LAYERS.beltMisalignment}
            graphs={visualAIBeltGraphs}
            selectedSections={visualAIBeltManager.selectedSections}
            onSelectionChange={visualAIBeltManager.handleSelectionChange}
            triggerReset={visualAIBeltManager.triggerReset}
            onResetDataLayer={() => {
              visualAIBeltManager.handleDataLayerReset();
              setTriggerVisualAIReset(false);
            }}
            loadingState={visualAIBeltManager.loadingState}
            queryParams={{ ...enhancedQueryParams, data_layer: SENSOR_FUSION_DATA_LAYERS.beltMisalignment }}
            forceRefresh={forceDataRefresh}
            onLoadingStateChange={(isLoading: boolean) => {
              visualAIBeltManager.handleLoadingStateChange(isLoading);
              handleVisualAILoadingStateChange(isLoading);
            }}
            includeAlarms={false}
            variant="lines"
            // verticalLinesState={verticalLinesState}
            // Focus mode props
            showLegendModeToggle={true}
            focusedSensors={focusedSensors}
            onFocus={onFocus}
            onToggleFocusMode={legendSync?.toggleMode}
            showFloatingToolbar={false}
            isTabView={isTabView}
            showSectionControls={false} //FIXME: add from api
            isExpandable={false} //FIXME: add from api
          />
        );
      },
    },
    {
      id: SENSOR_FUSION_DATA_LAYERS.rockSize,
      condition: () =>
        sectionPrimaryDataLayerId === SENSOR_FUSION_DATA_LAYERS.rockSize &&
        activeDataLayers.includes(SENSOR_FUSION_DATA_LAYERS.rockSize),
      component: () => {
        if (!visualAIRockGraphs || !visualAIRockManager) return null;
        return (
          <ManagedGraphsSection
            hideManagedGraphsDropdown={!isTabView}
            title={config.title}
            dataLayer={SENSOR_FUSION_DATA_LAYERS.rockSize}
            graphs={visualAIRockGraphs}
            selectedSections={visualAIRockManager.selectedSections}
            onSelectionChange={visualAIRockManager.handleSelectionChange}
            triggerReset={visualAIRockManager.triggerReset}
            onResetDataLayer={() => {
              visualAIRockManager.handleDataLayerReset();
              setTriggerVisualAIReset(false);
            }}
            loadingState={visualAIRockManager.loadingState}
            queryParams={{ ...enhancedQueryParams, data_layer: SENSOR_FUSION_DATA_LAYERS.rockSize }}
            forceRefresh={forceDataRefresh}
            onLoadingStateChange={(isLoading: boolean) => {
              visualAIRockManager.handleLoadingStateChange(isLoading);
              handleVisualAILoadingStateChange(isLoading);
            }}
            includeAlarms={false}
            variant="lines"
            // verticalLinesState={verticalLinesState}
            // Focus mode props
            showLegendModeToggle={true}
            focusedSensors={focusedSensors}
            onFocus={onFocus}
            onToggleFocusMode={legendSync?.toggleMode}
            showFloatingToolbar={false}
            isTabView={isTabView}
            showSectionControls={false} //FIXME: add from api
            isExpandable={false} //FIXME: add from api
          />
        );
      },
    },
  ];
};

/**
 * Render order for data layers
 */
export const DATA_LAYERS_RENDER_ORDER = [
  SENSOR_FUSION_DATA_LAYERS.beltMisalignment,
  SENSOR_FUSION_DATA_LAYERS.rockSize,
];
